import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import { fetchShopifyStatus } from "../../../services/shopifyApi";
import DisconnectShopifyButton from "./DisconnectShopifyButton";

const ShopifyDetailsDialog = ({ open, onClose, onDisconnected }) => {
  const workspaceId = useSelector(
    (s) => s.workspace?.currentWorkspace?._id || s.auth?.user?.workspaceId
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      setError("No workspace selected.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setError("Not authenticated.");
        return;
      }
      const data = await fetchShopifyStatus(workspaceId, token);
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to load Shopify details.");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // The Redux refresh (currentWorkspace.shopifyConnected) that the Integrations
  // card's isConnected reads from is the parent's job — onDisconnected is
  // Integrations.jsx's handleShopifyDisconnected, shared with the card-level
  // DisconnectShopifyButton so both entry points refresh the same way.
  const handleDisconnected = () => {
    onDisconnected?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { bgcolor: "background.paper" } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "1.4rem", fontWeight: 700, pt: 3, px: 3 }}>
        Shopify Integration Details
        <IconButton onClick={onClose} size="medium">
          <CloseIcon fontSize="medium" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            size="medium"
            startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading}
            variant="outlined"
            sx={{ textTransform: "none", fontSize: "0.95rem" }}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: "0.95rem" }}>
            {error}
          </Alert>
        )}

        {loading && !status && !error && (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {status && (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
                {status.shopDomain}
              </Typography>
              <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                <Chip
                  label={status.status === "connected" ? "Connected" : status.status || "Unknown"}
                  color={status.status === "connected" ? "success" : "default"}
                  variant="outlined"
                  sx={{ fontSize: "0.9rem", height: 32 }}
                />
              </Stack>
            </Box>

            {status.lastError?.message && (
              <Alert severity="warning" sx={{ fontSize: "0.95rem" }}>{status.lastError.message}</Alert>
            )}

            <Divider />

            <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1rem" }}>
              Connected: {status.installedAt ? new Date(status.installedAt).toLocaleString() : "—"}
            </Typography>

            <DisconnectShopifyButton onDisconnected={handleDisconnected} variant="outlined" size="large" />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShopifyDetailsDialog;
